/**
 * M83 — damage / Coins floater presentation hierarchy.
 * Presentation only: does not change dealt amounts or sim aggregation.
 */

export type DamageNumberStyle = 'direct' | 'splash' | 'dot' | 'shield' | 'amplified'

/** Visual tier — shape/size/color; amount text stays the rounded dealt value. */
export type DamageFloaterTier = 'ordinary' | 'strong' | 'shield' | 'dot' | 'amplified'

/** Direct hits at/above this read as "strong" (splash always strong). */
export const DAMAGE_STRONG_THRESHOLD = 28

export function resolveDamageFloaterTier(
  style: DamageNumberStyle,
  amount: number
): DamageFloaterTier {
  if (style === 'shield') return 'shield'
  if (style === 'dot') return 'dot'
  if (style === 'amplified') return 'amplified'
  if (style === 'splash' || amount >= DAMAGE_STRONG_THRESHOLD) return 'strong'
  return 'ordinary'
}

export interface DamageFloaterLook {
  tier: DamageFloaterTier
  /** CSS color string for Phaser Text. */
  color: string
  /** Prefix glyph (shape language via characters, not hue alone). */
  prefix: string
  /** Font size in px. */
  fontSize: number
  /** Rise distance in world px. */
  rise: number
  /** Tween duration in sim-ms (caller scales with sim clock). */
  simMs: number
}

export function damageFloaterLook(
  style: DamageNumberStyle,
  amount: number,
  cellSize: number,
  reducedMotion: boolean
): DamageFloaterLook {
  const tier = resolveDamageFloaterTier(style, amount)
  const base = Math.max(10, Math.floor(cellSize * 0.34))
  switch (tier) {
    case 'shield':
      return {
        tier,
        color: '#94a3b8',
        prefix: 'S',
        fontSize: base,
        rise: reducedMotion ? 12 : 22,
        simMs: 520
      }
    case 'dot':
      return {
        tier,
        color: '#a3e635',
        prefix: '~',
        fontSize: Math.max(9, Math.floor(base * 0.88)),
        rise: reducedMotion ? 10 : 18,
        simMs: 400
      }
    case 'amplified':
      return {
        tier,
        color: '#e9d5ff',
        prefix: '×',
        fontSize: Math.max(11, Math.floor(base * 1.08)),
        rise: reducedMotion ? 14 : 30,
        simMs: 640
      }
    case 'strong':
      return {
        tier,
        color: '#fbbf24',
        prefix: '',
        fontSize: Math.max(11, Math.floor(base * 1.12)),
        rise: reducedMotion ? 14 : 32,
        simMs: 640
      }
    default:
      return {
        tier,
        color: '#f8fafc',
        prefix: '',
        fontSize: base,
        rise: reducedMotion ? 12 : 26,
        simMs: 580
      }
  }
}

/** Match-currency floater copy — Coins, never "Gold". */
export function formatCoinsFloater(amount: number): string {
  return `+${Math.round(amount)}`
}
